import { useContext, useState } from "react";
import AutopilotCuratedSearchContext from "../../../contexts/AutopilotCuratedSearchContext";
import PastCuratedSearchSelector from "./CuratedSearchPromptEdit/PastCuratedSearchSelector";
import { CursorArrowRippleIcon } from "@heroicons/react/24/outline";
import classNames from "classnames";
import CuratedSearchCardHeader from "../CuratedSearchCard/CuratedSearchCardHeader";
import SuggestedPersonaPills from "./CuratedSearchPromptEdit/SuggestedPersonaPills";
import CuratedSearchPromptEditDescription from "./CuratedSearchPromptEdit/CuratedSearchPromptEditDescription";
import CiroButton, { CiroButtonStyleEnum } from "../../shared/CiroButton";
import SkeletonLoading from "../../shared/SkeletonLoading";

const HEADER_TEXT = "Search";
const HEADER_ICON = (
  <CursorArrowRippleIcon
    className={classNames("w-6", "h-6", "text-orange-500")}
  />
);

const CuratedSearchPromptSection = ({ loading }: { loading: boolean }) => {
  const {
    setSelectedPrompt,
    selectedPrompt,
    historicalPrompts,
    setStartedGeneratingFilters,
    startedGeneratingFilters,
    reset,
  } = useContext(AutopilotCuratedSearchContext);

  const [error, setError] = useState<string | null>(null);

  const handlePromptChange = (prompt: string) => {
    setSelectedPrompt(prompt);
    if (prompt) {
      setError("");
    }
  };

  if (loading) {
    return (
      <div>
        <CuratedSearchCardHeader
          headerText={HEADER_TEXT}
          headerIcon={HEADER_ICON}
        />
        <SkeletonLoading numSkeletons={5} skeletonHeight="1.5rem" />
      </div>
    );
  }

  return (
    <div className={classNames("flex", "flex-col", "gap-3")}>
      <CuratedSearchCardHeader
        headerText={HEADER_TEXT}
        headerIcon={HEADER_ICON}
        actionComponent={
          startedGeneratingFilters ? (
            <CiroButton
              analyticsField={"Cancel generating filters"}
              style={CiroButtonStyleEnum.LOUD}
              onClick={reset}
            >
              Restart
            </CiroButton>
          ) : (
            <PastCuratedSearchSelector
              historicalPrompts={historicalPrompts}
              setSelectedPrompt={handlePromptChange}
            />
          )
        }
      />
      {!startedGeneratingFilters && (
        <div>
          <div className={classNames("flex", "flex-col", "gap-4", "pt-4")}>
            <CuratedSearchPromptEditDescription
              error={error}
              setError={setError}
              prompt={selectedPrompt}
              setPrompt={handlePromptChange}
            />
            <SuggestedPersonaPills onSelectPersona={handlePromptChange} />
          </div>
          <div
            className={classNames(
              "flex",
              "flex-row",
              "justify-end",
              "gap-2",
              "pt-4",
            )}
          >
            <CiroButton
              analyticsField={"Apply prompt updates to filters"}
              style={CiroButtonStyleEnum.LOUD}
              disabled={!selectedPrompt}
              onClick={() => setStartedGeneratingFilters(true)}
            >
              Let's go
            </CiroButton>
          </div>
        </div>
      )}
      {startedGeneratingFilters && (
        <div className={classNames("text-sm", "text-zinc-500", "italic")}>
          {selectedPrompt}
        </div>
      )}
    </div>
  );
};

export default CuratedSearchPromptSection;
